/**
 * ===================================================================================
 * 📁 경로: /services/frontend/src/components/DuplicateFoundModal.js
 * 📌 프로그램명: 중복 파일 발견 안내 모달 컴포넌트
 * 🧩 개요:
 * - 사용자가 이전에 분석한 적 있는 동일한 PDF 파일을 다시 업로드했을 때,
 * 이를 사용자에게 알리고 다음 행동(기존 리포트 보기 또는 새로 분석하기)을
 * 선택할 수 있도록 안내하는 재사용 가능한 모달창 UI 컴포넌트입니다.
 * ===================================================================================
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * [Why]
 * 사용자가 동일한 파일을 중복해서 업로드하여 발생하는 불필요한 AI 분석 비용과
 * 시간 낭비를 방지하고, 사용자에게 명확한 다음 행동을 안내하여 긍정적인 서비스 경험을
 * 제공하기 위해 사용됩니다.
 *
 * [How]
 * - 부모 컴포넌트로부터 받은 `isOpen` prop에 따라 모달의 표시 여부가 결정됩니다.
 * - 사용자가 '분석리포트 보기' 버튼을 클릭하면, `onViewReport` 콜백 함수나 `Maps`를
 * 사용하여 기존 리포트 상세 페이지로 이동시킵니다.
 * - 사용자가 '새로 분석하기' 버튼을 클릭하면, `onReAnalyze` 콜백 함수를 호출하여
 * 부모 컴포넌트가 강제 재분석 로직을 실행하도록 합니다.
 * - 버튼의 라벨과 액션은 props를 통해 커스터마이징이 가능하여 재사용성을 높였습니다.
 *
 * @component
 * @param {object} props - 이 컴포넌트가 받는 데이터 객체입니다.
 * @property {boolean} props.isOpen - 모달의 열림/닫힘 상태.
 * @property {Function} props.onClose - 모달을 닫을 때 호출되는 콜백 함수.
 * @property {number | string} props.reportId - 기존에 분석된 리포트의 ID.
 * @property {Function} [props.onViewReport] - '리포트 보기' 버튼 클릭 시 실행될 커스텀 액션.
 * @property {Function} [props.onReAnalyze] - '새로 분석하기' 버튼 클릭 시 실행될 커스텀 액션.
 * @property {string} [props.primaryLabel] - 기본 액션 버튼의 커스텀 라벨.
 * @property {Function} [props.primaryAction] - 기본 액션 버튼의 커스텀 핸들러.
 * @property {string} [props.secondaryLabel] - 보조 액션 버튼의 커스텀 라벨.
 * @property {Function} [props.secondaryAction] - 보조 액션 버튼의 커스텀 핸들러.
 * @returns {JSX.Element | null} `isOpen`과 `reportId`가 모두 유효할 때만 모달 JSX 엘리먼트를 반환합니다.
 */
function DuplicateFoundModal({
    isOpen,
    onClose,
    reportId,
    onViewReport,
    onReAnalyze,
    // 새 props: 라벨/액션 커스터마이즈 (기본값은 기존 동작 유지)
    primaryLabel,
    primaryAction,
    secondaryLabel,
    secondaryAction,
}) {
    const navigate = useNavigate();

    if (!isOpen || !reportId) return null;

    /**
     * [Why]
     * 사용자가 기존에 생성된 분석 리포트를 확인하고자 할 때 실행되는 핸들러입니다.
     *
     * [How]
     * `onViewReport` prop이 제공되면 해당 함수를 우선적으로 실행하고, 그렇지 않으면
     * `react-router-dom`의 `Maps` 함수를 사용하여 `reportId`에 해당하는
     * 리포트 상세 페이지(`/reports/{reportId}`)로 이동시킵니다.
     */
    const handleViewReport = () => {
        if (typeof primaryAction === 'function') {
            primaryAction(reportId);
        } else if (typeof onViewReport === 'function') {
            onViewReport(reportId);
        } else {
            navigate(`/reports/${reportId}`);
        }
        onClose && onClose();
    };

    /**
     * [Why]
     * 사용자가 동일한 파일에 대해 새로운 AI 분석을 강제로 시작하고자 할 때 실행되는 핸들러입니다.
     *
     * [How]
     * `onReAnalyze` prop으로 전달된 함수를 `reportId`와 함께 호출하여
     * 부모 컴포넌트에게 재분석이 필요함을 알립니다.
     */
    const handleSecondAction = () => {
        if (typeof secondaryAction === 'function') {
            secondaryAction(reportId);
            onClose && onClose();
            return;
        }
        if (typeof onReAnalyze === 'function') {
            onReAnalyze(reportId);
            onClose && onClose();
        } else {
            navigate(`/action-plans/report/${reportId}`);
            onClose && onClose();
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3>📋 분석 리포트 및 실천 계획이 있으시네요.</h3>
                <p>
                    최근에 건강검진을 받으셨다면<br />
                    새로 분석하기 버튼을 눌러주세요.
                </p>
                <div className="modal-actions">
                    <button className="btn btn-primary" onClick={handleViewReport}>{primaryLabel || '분석리포트 보기'}</button>
                    <button className="btn btn-outline" onClick={handleSecondAction}>{secondaryLabel || (onReAnalyze ? '새로 분석하기' : '실천계획보기')}</button>
                </div>
                <button onClick={onClose} className="btn btn-outline close-button">닫기</button>
            </div>
        </div>
    );
}

export default DuplicateFoundModal;